import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeftIcon, CheckIcon, ArchiveBoxIcon, TrashIcon } from '@heroicons/react/20/solid'
import { webLeadsApi } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'

const INTERESTS = {
  discovery_day: 'Discovery Day',
  pilot_project: 'Pilot Project',
  partnership_request: 'Partnership Request',
  general_question: 'General Question',
} as const

function WebLeadStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    unviewed: 'bg-red-100 text-red-800',
    viewed: 'bg-blue-100 text-blue-800',
    converted: 'bg-green-100 text-green-800',
    archived: 'bg-gray-100 text-gray-800',
  }
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  )
}

function InterestBadge({ interest }: { interest: string }) {
  const colors: Record<string, string> = {
    discovery_day: 'bg-purple-100 text-purple-800',
    pilot_project: 'bg-indigo-100 text-indigo-800',
    partnership_request: 'bg-amber-100 text-amber-800',
    general_question: 'bg-cyan-100 text-cyan-800',
  }
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors[interest] || 'bg-gray-100 text-gray-800'}`}>
      {INTERESTS[interest as keyof typeof INTERESTS] || interest}
    </span>
  )
}

export function WebLeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { data: webLead, isLoading } = useQuery({
    queryKey: ['web-lead', id],
    queryFn: () => webLeadsApi.get(id!),
    enabled: !!id,
  })

  const markViewedMutation = useMutation({
    mutationFn: () => webLeadsApi.markViewed(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['web-lead', id] })
      queryClient.invalidateQueries({ queryKey: ['web-leads'] })
      queryClient.invalidateQueries({ queryKey: ['web-leads-stats'] })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: () => webLeadsApi.archive(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['web-lead', id] })
      queryClient.invalidateQueries({ queryKey: ['web-leads'] })
      queryClient.invalidateQueries({ queryKey: ['web-leads-stats'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => webLeadsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['web-leads'] })
      queryClient.invalidateQueries({ queryKey: ['web-leads-stats'] })
      navigate('/web-leads')
    },
  })

  if (isLoading) return <div className="p-6">{t('common.loading')}</div>
  if (!webLead) return <div className="p-6">{t('common.notFound')}</div>

  return (
    <div className="p-4 xl:p-6">
      <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)]">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/web-leads"
                className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                {t('common.back')}
              </Link>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">{webLead.name}</h1>
                <p className="text-sm text-gray-500">{webLead.company}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <WebLeadStatusBadge status={webLead.status} />
              {webLead.status === 'unviewed' && (
                <button
                  onClick={() => markViewedMutation.mutate()}
                  disabled={markViewedMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4" />
                  {t('pages.webLeads.actions.markViewed')}
                </button>
              )}
              {webLead.status !== 'archived' && (
                <button
                  onClick={() => archiveMutation.mutate()}
                  disabled={archiveMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 disabled:opacity-50"
                >
                  <ArchiveBoxIcon className="h-4 w-4" />
                  {t('common.archive')}
                </button>
              )}
              <button
                onClick={() => {
                  if (confirm(t('common.confirmDelete'))) {
                    deleteMutation.mutate()
                  }
                }}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-red-100 px-4 py-2 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-200 disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" />
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {/* Column 1: Contact & Inquiry */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">{t('pages.webLeads.sections.contact')}</h2>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('fields.name')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {webLead.firstName && webLead.lastName
                        ? `${webLead.firstName} ${webLead.lastName}`
                        : webLead.name}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('fields.organisation')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{webLead.company}</dd>
                  </div>
                  {webLead.jobTitle && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.jobTitle')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">{webLead.jobTitle}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('fields.email')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <a href={`mailto:${webLead.email}`} className="text-brand-600 hover:text-brand-900">
                        {webLead.email}
                      </a>
                    </dd>
                  </div>
                  {webLead.phone && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('fields.phone')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        <a href={`tel:${webLead.phone}`} className="text-brand-600 hover:text-brand-900">
                          {webLead.phone}
                        </a>
                      </dd>
                    </div>
                  )}
                  {webLead.preferredContactMethod && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.preferredContactMethod')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">{webLead.preferredContactMethod}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Inquiry */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">{t('pages.webLeads.sections.inquiry')}</h2>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.interest')}</dt>
                    <dd className="mt-1">
                      <InterestBadge interest={webLead.interest} />
                    </dd>
                  </div>
                  {webLead.productInterest && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.productInterest')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">{webLead.productInterest}</dd>
                    </div>
                  )}
                  {webLead.formName && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.formName')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">{webLead.formName}</dd>
                    </div>
                  )}
                  {webLead.downloadRequested && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.downloadRequested')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">{webLead.downloadRequested}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.message')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                      {webLead.message}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Column 2: Attribution & Visitor */}
            <div className="space-y-6">
              {/* Attribution */}
              {(webLead.utmSource || webLead.utmMedium || webLead.utmCampaign || webLead.referrerUrl) && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">{t('pages.webLeads.sections.attribution')}</h2>
                  <dl className="space-y-4">
                    {webLead.referrerUrl && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.referrerUrl')}</dt>
                        <dd className="mt-1 text-sm text-gray-900 truncate">
                          <a href={webLead.referrerUrl} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-900">
                            {webLead.referrerUrl}
                          </a>
                        </dd>
                      </div>
                    )}
                    {webLead.utmSource && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.utmSource')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.utmSource}</dd>
                      </div>
                    )}
                    {webLead.utmMedium && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.utmMedium')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.utmMedium}</dd>
                      </div>
                    )}
                    {webLead.utmCampaign && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.utmCampaign')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.utmCampaign}</dd>
                      </div>
                    )}
                    {webLead.utmContent && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.utmContent')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.utmContent}</dd>
                      </div>
                    )}
                    {webLead.utmTerm && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.utmTerm')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.utmTerm}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {/* Visitor Context */}
              {(webLead.country || webLead.timezone || webLead.browserLanguage) && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">{t('pages.webLeads.sections.visitor')}</h2>
                  <dl className="space-y-4">
                    {(webLead.country || webLead.city) && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.location')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {[webLead.city, webLead.region, webLead.country].filter(Boolean).join(', ')}
                        </dd>
                      </div>
                    )}
                    {webLead.timezone && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.timezone')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.timezone}</dd>
                      </div>
                    )}
                    {webLead.browserLanguage && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.browserLanguage')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.browserLanguage}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {/* Device & Browser */}
              {(webLead.deviceType || webLead.browserName || webLead.os) && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">{t('pages.webLeads.sections.device')}</h2>
                  <dl className="space-y-4">
                    {webLead.deviceType && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.deviceType')}</dt>
                        <dd className="mt-1 text-sm text-gray-900 capitalize">{webLead.deviceType}</dd>
                      </div>
                    )}
                    {webLead.os && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.os')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.os}</dd>
                      </div>
                    )}
                    {webLead.browserName && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.browser')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {webLead.browserName}{webLead.browserVersion ? ` ${webLead.browserVersion}` : ''}
                        </dd>
                      </div>
                    )}
                    {webLead.screenResolution && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.screenResolution')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.screenResolution}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>

            {/* Column 3: Session & Metadata */}
            <div className="space-y-6">
              {/* Session Data */}
              {(webLead.entryPage || webLead.pageCount || webLead.timeOnSite) && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">{t('pages.webLeads.sections.session')}</h2>
                  <dl className="space-y-4">
                    {webLead.entryPage && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.entryPage')}</dt>
                        <dd className="mt-1 text-sm text-gray-900 truncate">{webLead.entryPage}</dd>
                      </div>
                    )}
                    {webLead.pageCount != null && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.pageCount')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.pageCount}</dd>
                      </div>
                    )}
                    {webLead.timeOnSite != null && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.timeOnSite')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {Math.floor(webLead.timeOnSite / 60)}m {webLead.timeOnSite % 60}s
                        </dd>
                      </div>
                    )}
                    {webLead.scrollDepth != null && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.scrollDepth')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{webLead.scrollDepth}%</dd>
                      </div>
                    )}
                    {webLead.returningVisitor && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.returningVisitor')}</dt>
                        <dd className="mt-1 text-sm text-green-600">{t('common.yes')}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {/* Metadata */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">{t('pages.webLeads.sections.metadata')}</h2>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('fields.status')}</dt>
                    <dd className="mt-1">
                      <WebLeadStatusBadge status={webLead.status} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('fields.createdAt')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(webLead.createdAt).toLocaleString()}
                    </dd>
                  </div>
                  {webLead.viewedAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.viewedAt')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(webLead.viewedAt).toLocaleString()}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.source')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{webLead.websiteConfigName || '-'}</dd>
                  </div>
                  {webLead.sourceUrl && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.sourceUrl')}</dt>
                      <dd className="mt-1 text-sm text-gray-900 truncate">
                        <a href={webLead.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-900">
                          {webLead.sourceUrl}
                        </a>
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.privacyConsent')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {webLead.privacyConsent ? (
                        <span className="text-green-600">{t('common.yes')}</span>
                      ) : (
                        <span className="text-red-600">{t('common.no')}</span>
                      )}
                    </dd>
                  </div>
                  {(webLead.analyticsConsent != null || webLead.marketingConsent != null) && (
                    <>
                      {webLead.analyticsConsent != null && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.analyticsConsent')}</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {webLead.analyticsConsent ? (
                              <span className="text-green-600">{t('common.yes')}</span>
                            ) : (
                              <span className="text-red-600">{t('common.no')}</span>
                            )}
                          </dd>
                        </div>
                      )}
                      {webLead.marketingConsent != null && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.marketingConsent')}</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {webLead.marketingConsent ? (
                              <span className="text-green-600">{t('common.yes')}</span>
                            ) : (
                              <span className="text-red-600">{t('common.no')}</span>
                            )}
                          </dd>
                        </div>
                      )}
                    </>
                  )}
                </dl>
              </div>

              {/* Technical Info */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">{t('pages.webLeads.sections.technical')}</h2>
                <dl className="space-y-4">
                  {webLead.ipAddress && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.ipAddress')}</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">{webLead.ipAddress}</dd>
                    </div>
                  )}
                  {webLead.userAgent && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.userAgent')}</dt>
                      <dd className="mt-1 text-sm text-gray-500 text-xs font-mono break-all">
                        {webLead.userAgent}
                      </dd>
                    </div>
                  )}
                  {webLead.sessionId && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('pages.webLeads.fields.sessionId')}</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">{webLead.sessionId}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
